import { c as _c } from "react/compiler-runtime";
import { Card } from '@onefootprint/ui';
import type { Ref } from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type CitationPair, citationKey } from '../../../hooks/use-sources';
import type { ReportSegment, SplitReport } from '../../../utils/split-report-sections';
import { type ArtifactBlockKind, artifactBlockRegistry, isArtifactBlockKind } from '../artifact-blocks/artifact-block-registry';
import MarkdownView from './markdown-view';
import ReportSectionHeader from './report-section-header';
type ReportCardsViewProps = {
  report: SplitReport;
  investigationId?: string;
  /** Captures the cards for "Download as PDF". */
  contentRef?: Ref<HTMLDivElement>;
  onCitationPairsChange?: (pairs: CitationPair[]) => void;
  onOpenSources?: () => void;
  onOpenDocument?: (documentIdentifier: string) => void;
};

// The card's header already names the section, so a block repeating that name as its own title would
// print it twice. A block whose header can't go untitled keeps it: a data view requires a title, and an
// untitled decision reads "Investigation decision".
const dropRepeatedTitle = (markdown: string, kind: ArtifactBlockKind, sectionTitle: string) => {
  if (kind === 'decision') return markdown;
  const lines = markdown.split('\n');
  let config: unknown;
  try {
    config = JSON.parse(lines.slice(1, -1).join('\n'));
  } catch {
    return markdown;
  }
  if (!config || typeof config !== 'object' || !('title' in config)) return markdown;
  const {
    title,
    ...untitled
  } = config;
  if (typeof title !== 'string' || title.trim().toLowerCase() !== sectionTitle.trim().toLowerCase()) return markdown;
  const next = JSON.stringify(untitled);
  return artifactBlockRegistry[kind].parse(next) ? [lines[0], next, lines.at(-1)].join('\n') : markdown;
};
const keyOf = (pairs: CitationPair[]) => pairs.map(pair => citationKey(pair.sourceLabel, pair.locator)).join('\n');

/** A report laid out like the Generated Summary, one card per section. */
const ReportCardsView = t0 => {
  const $ = _c(6);
  const {
    report,
    investigationId,
    contentRef,
    onCitationPairsChange,
    onOpenSources,
    onOpenDocument
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "reportCards"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("investigations", t1);
  const pairsByRun = useRef(new Map());
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [reportPairs, setReportPairs] = useState(t2);
  const collectPairs = id => pairs => {
    pairsByRun.current.set(id, pairs);
    const merged = new Map();
    for (const list of pairsByRun.current.values()) {
      for (const pair of list) {
        merged.set(citationKey(pair.sourceLabel, pair.locator), pair);
      }
    }
    const next = [...merged.values()];
    setReportPairs(prev => keyOf(prev) === keyOf(next) ? prev : next);
    onCitationPairsChange?.(next);
  };
  const run = (id_0, markdown) => <MarkdownView embedded={true} content={markdown} investigationId={investigationId} onCitationPairsChange={collectPairs(id_0)} sourcePairs={reportPairs} onOpenSources={onOpenSources} onOpenDocument={onOpenDocument} />;
  const segment = (id_1, item, sectionTitle) => item.type === "block" && isArtifactBlockKind(item.kind) ? <div key={id_1} className="report-card-block">{run(id_1, dropRepeatedTitle(item.markdown, item.kind, sectionTitle))}</div> : <Card.Body key={id_1} className="report-card-content p-4 md:p-5">{run(id_1, item.markdown)}</Card.Body>;
  const t3 = "h-full overflow-y-auto overscroll-contain";
  const t4 = "max-w-(--main-content-max-width) mx-auto w-full pb-12 md:pb-0";
  const t5 = "flex flex-col gap-4 pt-5 pb-8 px-4 md:px-16";
  const t6 = report.intro && <div className="report-card-content">{run("intro", report.intro)}</div>;
  const t7 = report.sections.map((section, sectionIndex) => <Card.Root key={sectionIndex}><ReportSectionHeader title={section.title} />{section.segments.length === 0 ? <Card.Empty>{t(_temp)}</Card.Empty> : <div className="flex flex-col gap-1">{section.segments.map((item_0, index) => segment(`${sectionIndex}-${index}`, item_0, section.title))}</div>}</Card.Root>);
  let t8;
  if ($[2] !== contentRef || $[3] !== t6 || $[4] !== t7) {
    t8 = <section className={t3}><div className={t4}><div ref={contentRef} className={t5}>{t6}{t7}</div></div></section>;
    $[2] = contentRef;
    $[3] = t6;
    $[4] = t7;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  return t8;
};
export default ReportCardsView;
function _temp($) {
  return $.emptySection;
}